import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC, useMemo } from "react"
import { SourceKeyDto } from "src/features/things/reservationSyncApi"
import { DRIFT_CLASS_NOTE } from "./driftClasses"
import { fmtCount } from "./ReservationSyncChrome"
import { SOURCE_ORDER } from "./SourceCards"

/** One classified row, as the stored drift report writes it. */
export type PresenceRow = {
  sstId: string
  driftClass: string
  presence?: Record<SourceKeyDto, boolean>
  nonDriftFlags?: string[]
}

const TICK_COLOR = { present: "#2E6B4F", absent: "#A33A2E", none: "#C9C5BD" }

/**
 * The four-way presence matrix (LOCKER-421 `DoD4`, `DoD8`).
 *
 * Rows are grouped by the COMBINATION that occurs, not listed one per
 * reservation: a locker holds thousands, and the question this answers is "what
 * shapes of disagreement exist here", which a per-row table buries. The per-row
 * detail is the drift tab.
 *
 * Only combinations that actually occur are listed. A matrix that enumerated all
 * sixteen would be fifteen rows of zero on every healthy locker, and the one row
 * that mattered would be indistinguishable from the noise.
 */
export const PresenceMatrix: FC<{
  rows: PresenceRow[]
  /** True when the run refused to state a count — the matrix refuses too. */
  suppressed?: boolean
}> = ({ rows, suppressed }) => {
  const grouped = useMemo(() => {
    const byKey = new Map<
      string,
      { presence: Record<SourceKeyDto, boolean>; driftClass: string; count: number }
    >()
    for (const row of rows) {
      if (!row.presence) continue
      const key = `${SOURCE_ORDER.map((s) => (row.presence![s] ? "1" : "0")).join("")}|${row.driftClass}`
      const existing = byKey.get(key)
      if (existing) existing.count += 1
      else
        byKey.set(key, {
          presence: row.presence,
          driftClass: row.driftClass,
          count: 1,
        })
    }
    // Worst first: the combinations an operator has to act on, before the ones
    // that are already fine.
    return [...byKey.values()].sort((a, b) => {
      const rank = (c: string) => (c === "identical" ? 1 : 0)
      return rank(a.driftClass) - rank(b.driftClass) || b.count - a.count
    })
  }, [rows])

  if (suppressed)
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        No presence matrix is shown for an inconclusive run — every combination it
        would list describes rows nobody read.
      </Typography>
    )

  if (!grouped.length)
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Load the drift report to see the presence matrix — it is derived from the
        stored rows, part by part.
      </Typography>
    )

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            {SOURCE_ORDER.map((s) => (
              <TableCell key={s} align="center" sx={{ fontWeight: 700 }}>
                {s.toUpperCase()}
              </TableCell>
            ))}
            <TableCell align="right">Rows</TableCell>
            <TableCell>Reads as</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {grouped.map((g) => (
            <TableRow key={`${SOURCE_ORDER.map((s) => (g.presence[s] ? 1 : 0)).join("")}-${g.driftClass}`}>
              {SOURCE_ORDER.map((s) => (
                <TableCell
                  key={s}
                  align="center"
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 600,
                    color: g.presence[s]
                      ? TICK_COLOR.present
                      : g.driftClass === "identical"
                        ? TICK_COLOR.none
                        : TICK_COLOR.absent,
                  }}
                >
                  {/* A dot, not a cross, where absence is expected rather than a
                      finding — the v1 column on a v2-native row is not a gap. */}
                  {g.presence[s] ? "✓" : g.driftClass === "identical" ? "·" : "×"}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                {fmtCount(g.count)}
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {g.driftClass}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "text.secondary" }}
                >
                  {DRIFT_CLASS_NOTE[g.driftClass] ?? ""}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 1.5, color: "text.secondary" }}
      >
        Every reservation id joined on <code>sstId</code>. Only combinations that
        occur are listed.
      </Typography>
    </Box>
  )
}
